<script setup lang="ts">
import { computed } from 'vue';

import type { Location } from '@/entities/auth';

import { isLocationClosed, isLocationPublic } from '@/entities/location';
import { AppStatusChip } from '@/shared/ui';
import { IconPreloader } from '@/shared/ui/icons';

interface Props {
  location: Location;
  isSelected?: boolean;
  isLoading?: boolean;
}

const props = defineProps<Props>();

const isClosed = computed(() => isLocationClosed(props.location));

const emit = defineEmits<{
  select: [location: Location];
}>();
</script>

<template>
  <button
    type="button"
    class="ml-[20px] min-h-11 cursor-pointer rounded-[6px] border p-[10px] text-left transition-colors focus-visible:outline-2 focus-visible:outline-(--base-accent-color) disabled:cursor-wait"
    :class="{
      'opacity-40': isLoading && !isSelected,
      'bg-(--base-element-color)': isSelected,
      'bg-(--base-container-bg) hover:bg-(--base-popup-hover-color)': !isSelected,
    }"
    :style="{
      borderColor: 'var(--base-popup-border-color)',
    }"
    :aria-current="isSelected ? 'true' : undefined"
    :disabled="isLoading"
    @click="emit('select', location)"
  >
    <div class="flex justify-between">
      <div class="flex flex-col gap-y-[4px]">
        <div class="flex items-center gap-x-2">
          <div
            class="text-sm"
            style="color: var(--base-text-color)"
          >
            {{ location.title }}
          </div>
          <AppStatusChip
            v-if="isClosed"
            tone="neutral"
          >
            Закрыта
          </AppStatusChip>
          <span
            v-else-if="!isLocationPublic(location)"
            class="text-xs text-(--base-popup-secondary-color)"
            >Скрыта из онлайн-записи</span
          >
        </div>
        <div
          v-if="location.full_address"
          class="text-xs"
          style="color: var(--base-popup-secondary-color)"
        >
          {{ location.full_address }}
        </div>
      </div>
      <div class="flex items-center pr-[12px]">
        <IconPreloader
          v-if="isSelected"
          class="text-(--base-accent-color)"
        />
      </div>
    </div>
  </button>
</template>
